import { useState, useCallback } from "react";
import {
    listStatusesApi,
    createTaskStatusApi,
    updateTaskStatusApi,
    deleteTaskStatusApi
} from "../../services/tasksService";

export function useTaskStatuses({ token, workspaceId }) {
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchStatuses = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        try {
            // Note: Service renamed to listStatusesApi
            const { listStatusesApi } = await import("../../services/tasksService");
            const data = await listStatusesApi({ token, workspaceId });
            setTaskStatuses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Failed to load statuses.");
        } finally {
            setLoading(false);
        }
    }, [token, workspaceId]);

    const createStatus = async (name, color, isCompleted = false) => {
        try {
            const { createTaskStatusApi } = await import("../../services/tasksService");
            const newStatus = await createTaskStatusApi({ token, workspaceId, data: { name, color, is_completed: isCompleted } });
            setTaskStatuses(prev => [...prev, newStatus]);
            return newStatus;
        } catch (err) {
            console.error("Failed to create status", err);
            throw err;
        }
    };

    const updateStatus = async (statusId, data) => {
        // Optimistic
        setTaskStatuses(prev => prev.map(s => s.id === statusId ? { ...s, ...data } : s));
        try {
            await updateTaskStatusApi({ token, workspaceId, statusId, data });
        } catch (err) {
            console.error("Failed to update status", err);
            // Revert logic could be added here
        }
    };

    const deleteStatus = async (statusId) => {
        try {
            await deleteTaskStatusApi({ token, workspaceId, statusId });
            setTaskStatuses(prev => prev.filter(s => s.id !== statusId));
        } catch (err) {
            console.error("Failed to delete status", err);
            throw new Error("Failed to delete status. Ensure no tasks are using it.");
        }
    };

    return {
        taskStatuses,
        loading,
        error,
        fetchStatuses,
        createStatus,
        updateStatus,
        deleteStatus,
        setTaskStatuses // Exposed for manual updates if needed
    };
}
